import React, { useState, useEffect, useRef } from 'react';
import Header from "./Header"; // ✅ import Header
import Footer from "./Footer"; // ✅ import Footer

// --- HELPER & UTILITY COMPONENTS ---
const useOnScreen = (options) => {
  const ref = useRef();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current; 
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      }
    }, options);

    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [ref, options]);

  return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
  const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
  return (
    <section
      ref={ref}
      id={id}
      className={`${className} transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      {children}
    </section>
  );
};

const PageHeader = ({ title, subtitle }) => (
  <section className="relative py-24 bg-slate-50">
    <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
        {title}
      </h1>
      <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
    </div>
  </section>
);

// --- ICONS ---
const Briefcase = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);
const BarChart2 = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);
const Globe = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const CareersPage = () => (
  <>
    <Header /> {/* ✅ Header at top */}
    <main>
      <PageHeader
        title="Join Our Mission"
        subtitle="We're looking for talented, passionate people to help us redefine the future of business communication."
      />

      {/* Why Join Us Section */}
      <AnimatedSection className="py-24" id="why-join-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-slate-900">
              Why You'll Love Working Here
            </h2>
          </div>
          <div className="mt-16 grid gap-10 md:grid-cols-3">
            <div className="text-center p-8 bg-white rounded-2xl shadow-lg border">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mx-auto">
                <BarChart2 className="h-8 w-8" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-slate-900">
                Drive Innovation
              </h3>
              <p className="mt-2 text-base text-slate-600">
                Work on cutting-edge AI technology that's changing an industry.
              </p>
            </div>
            <div className="text-center p-8 bg-white rounded-2xl shadow-lg border">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mx-auto">
                <Globe className="h-8 w-8" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-slate-900">
                Flexible Work
              </h3>
              <p className="mt-2 text-base text-slate-600">
                We offer remote and hybrid options to fit your lifestyle.
              </p>
            </div>
            <div className="text-center p-8 bg-white rounded-2xl shadow-lg border">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mx-auto">
                <Briefcase className="h-8 w-8" />
              </div>
              <h3 className="mt-6 text-xl font-bold text-slate-900">
                Grow With Us
              </h3>
              <p className="mt-2 text-base text-slate-600">
                Benefit from a clear career path and professional development
                opportunities.
              </p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Open Positions */}
      <AnimatedSection className="py-24 bg-slate-50" id="open-positions">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-slate-900">
              Open Positions
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">
              We are always looking for great talent. See our current openings
              below.
            </p>
          </div>
          <div className="mt-12 space-y-8">
            {[
              {
                department: 'Engineering',
                roles: [
                  { title: 'Senior Backend Engineer', location: 'Remote' },
                  { title: 'Frontend Developer (React)', location: 'Noida, India' },
                ],
              },
              {
                department: 'Sales',
                roles: [{ title: 'Account Executive', location: 'Remote, US' }],
              },
              {
                department: 'Marketing',
                roles: [
                  { title: 'Content Marketing Manager', location: 'Remote' },
                ],
              },
            ].map((dept) => (
              <div key={dept.department}>
                <h3 className="text-2xl font-bold text-slate-800 border-b pb-2 mb-4">
                  {dept.department}
                </h3>
                <div className="space-y-4">
                  {dept.roles.map((role) => (
                    <div
                      key={role.title}
                      className="bg-white p-6 rounded-lg shadow-md flex justify-between items-center transition-shadow hover:shadow-lg"
                    >
                      <div>
                        <h4 className="font-bold text-lg text-blue-700">
                          {role.title}
                        </h4>
                        <p className="text-slate-500">{role.location}</p>
                      </div>
                      <button className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
                        Apply Now
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <p className="text-slate-600">
              Don't see a role that fits? We'd still love to hear from you.
            </p>
            <a
              href="mailto:careers@hlfppt.org "
              className="font-semibold text-blue-600 hover:underline"
            >
              Send us your resume
            </a>
          </div>
        </div>
      </AnimatedSection>
    </main>
    <Footer /> {/* ✅ Footer at bottom */}
  </>
);

export default CareersPage;
